import { Box, Paper, Typography, useTheme } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuChevronRight } from "react-icons/lu";
import foot from "../../../assets/logo/foot.png";
import AgGrid from "../../../components/grid/AgGrid";
import { defaultColDef, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import config from "../../../config";
import { getOrgTokens } from "../../../theme/style/organizationPalette";
import { dedupeMaterials, withMaterialCompositionPercentage } from "./dedupeMaterials";
import { dppSectionList as sectionList } from "./dppPassportSections";
import { dpp_format as defaultDppFormat } from "../../Products/DPP/ProductDPP_Format";

const GREEN = "#659d33";

const usePreviewTok = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  return { t, theme, isDark, tok };
};

function SubHeading({ children }) {
  const { tok } = usePreviewTok();
  return (
    <Typography sx={{ mt: 2, mb: 1, fontSize: "13px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left" }}>
      {children}
    </Typography>
  );
}

/** Attribute/Value rows rendered through the shared AgGrid. */
function KVTable({ rows }) {
  const colDefs = useMemo(() => [
    {
      headerName: "Attribute",
      field: "field",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 200,
      flex: 1,
      cellStyle: {
        ...leftAlignColDef.cellStyle,
        fontSize: "14px",
      },
    },
    {
      headerName: "Value",
      field: "value",
      ...leftAlignColDef,
      ...defaultColDef,
      minWidth: 220,
      flex: 2,
      cellStyle: (params) => (typeof params.value === "number" ? rightAlignColDef.cellStyle : leftAlignColDef.cellStyle),
      valueFormatter: (params) => {
        const v = params.value;
        if (v === undefined || v === null || v === "" || (typeof v === "number" && isNaN(v))) return "-";
        return typeof v === "number" ? v.toLocaleString() : String(v);
      },
    },
  ], []);

  const rowData = useMemo(() => rows.map(([k, v], i) => ({ id: i, field: k, value: v })), [rows]);

  return (
    <Box
      sx={{
        my: 1,
        mx: -3,
        // ag-header-cell has a global 16px paddingLeft (!important), but
        // leftAlignColDef's cellStyle sets cell text paddingLeft to 20px —
        // that 4px gap made header labels sit left of their column's values.
        "& .ag-header-cell.ag-left-aligned-header": { paddingLeft: "20px !important" },
      }}
    >
      <AgGrid columnDefs={colDefs} rowData={rowData} getRowId={(params) => String(params.data.id)} domLayout="autoHeight" suppressPagination headerCheckbox={false} />
    </Box>
  );
}

/** Repeatable list fields (materials, certifications, ...) rendered as an AgGrid table. */
function ListTable({ itemDef, items, pagination = false }) {
  const colDefs = useMemo(() => itemDef.map((def) => ({
    headerName: def.label,
    field: def.field,
    ...(def.type === "float" ? rightAlignColDef : leftAlignColDef),
    ...defaultColDef,
    minWidth: 140,
    flex: 1,
    // Override defaultColDef's wrapHeaderText/autoHeaderHeight — long
    // labels like "Material Composition Percentage" were wrapping to 3
    // lines and making the header row unnecessarily tall.
    wrapHeaderText: false,
    autoHeaderHeight: false,
    ...(def.type === "boolean"
      ? { valueFormatter: (p) => (p.value ? "Yes" : "No") }
      : def.type === "float"
        ? { valueFormatter: (p) => (p.value === undefined || p.value === null || p.value === "" || isNaN(p.value) ? "-" : Number(p.value).toLocaleString()) }
        : { valueFormatter: (p) => (p.value === undefined || p.value === null || p.value === "" ? "-" : String(p.value)) }),
  })), [itemDef]);

  const rowData = useMemo(() => (items || []).map((m, i) => ({ id: i, ...m })), [items]);

  if (!rowData.length) {
    return <Typography sx={{ fontSize: "12.5px", color: "#94a3b8", fontFamily: "Poppins, sans-serif", textAlign: "left" }}>-</Typography>;
  }
  return (
    <Box
      sx={{
        my: 1,
        mx: -3,
        // ag-header-cell has a global 16px paddingLeft (!important), but
        // leftAlignColDef's cellStyle sets cell text paddingLeft to 20px —
        // that 4px gap made header labels sit left of their column's values.
        "& .ag-header-cell.ag-left-aligned-header": { paddingLeft: "20px !important" },
      }}
    >
      <AgGrid columnDefs={colDefs} rowData={rowData} getRowId={(params) => String(params.data.id)} domLayout="autoHeight" suppressPagination={!pagination} headerCheckbox={false} />
    </Box>
  );
}

const formatPrintCell = (value, type) => {
  if (value === undefined || value === null || value === "") return "-";
  if (type === "boolean") return value ? "Yes" : "No";
  if (type === "float") {
    const n = Number(value);
    return isNaN(n) ? "-" : n.toLocaleString();
  }
  return String(value);
};

/** Plain-HTML equivalent of KVTable — used only for the PDF's fullMode render, since AgGrid's web-app chrome (scrollbars, virtualized rows) doesn't capture well as a static document. */
function PrintKVTable({ rows }) {
  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", mt: 1, mb: 0.5 }}>
      <Box component="tbody">
        {rows.map(([k, v], i) => (
          <Box component="tr" key={i} sx={{ backgroundColor: i % 2 === 0 ? "#f7f8f6" : "#fff" }}>
            <Box component="td" sx={{ py: "7px", px: "10px", fontSize: "10px", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", width: "40%", borderBottom: "1px solid #e5e7eb", verticalAlign: "top" }}>
              {k}
            </Box>
            <Box component="td" sx={{ py: "7px", px: "10px", fontSize: "11.5px", fontWeight: 500, color: "#1e293b", borderBottom: "1px solid #e5e7eb", wordBreak: "break-word" }}>
              {v === undefined || v === null || v === "" ? "-" : String(v)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Plain-HTML equivalent of ListTable — same reasoning as PrintKVTable. */
function PrintListTable({ itemDef, items }) {
  if (!items?.length) {
    return <Typography sx={{ fontSize: "11.5px", color: "#94a3b8", fontFamily: "Poppins, sans-serif", textAlign: "left" }}>-</Typography>;
  }
  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", mt: 1, mb: 0.5 }}>
      <Box component="thead">
        <Box component="tr">
          {itemDef.map((def) => (
            <Box component="th" key={def.field} sx={{ textAlign: def.type === "float" ? "right" : "left", py: "7px", px: "10px", fontSize: "9.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "#fff", backgroundColor: GREEN }}>
              {def.label}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {items.map((item, i) => (
          <Box component="tr" key={i} sx={{ backgroundColor: i % 2 === 0 ? "#f7f8f6" : "#fff" }}>
            {itemDef.map((def) => (
              <Box component="td" key={def.field} sx={{ textAlign: def.type === "float" ? "right" : "left", py: "6px", px: "10px", fontSize: "11px", color: "#334155", borderBottom: "1px solid #e5e7eb", wordBreak: "break-word" }}>
                {formatPrintCell(item[def.field], def.type)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const ProductDPP_Preview = ({ dpp, photoPreview, entityId, fullMode = false, qrValue, renameLabel = (s) => s, photoRefreshSignal, dppFormat = defaultDppFormat, nameField = "product_name" }) => {
  const { t, tok, isDark } = usePreviewTok();
  const myUrl = window.location.href;
  const [selected, setSelected] = useState(sectionList[0].key);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (!entityId) { setPhotos([]); return; }
    fetch(`${config.apiUrl}/api/fetch_emission_reference_files?ID=${entityId}`)
      .then((res) => res.json())
      .then((data) => setPhotos(data.filter((f) => f.scope === "DPP Evidence" && f.is_photo && !f.hidden_in_photo_section)))
      .catch(() => {});
  }, [entityId, photoRefreshSignal]);

  const sectionBody = (dppSection, printMode = false) => {
    if (!dpp || !dppSection) return null;
    const fields = dppFormat.filter((d) => d.section === dppSection);
    const scalarRows = fields
      .filter((d) => ["string", "float", "boolean"].includes(d.type))
      .map((d) => [renameLabel(d.label), d.type === "boolean" ? (dpp[d.field] ? "Yes" : "No") : dpp[d.field]]);
    const listFields = fields.filter((d) => d.type === "list");
    const KVComp = printMode ? PrintKVTable : KVTable;
    const ListComp = printMode ? PrintListTable : ListTable;
    return (
      <>
        {scalarRows.length > 0 && <KVComp rows={scalarRows} />}
        {listFields.map((d) => (
          <Box key={d.field} sx={{ mt: scalarRows.length ? 2 : 0 }}>
            <SubHeading>{t(renameLabel(d.label))}</SubHeading>
            <ListComp
              itemDef={d.itemDef.map((def) => ({ ...def, label: renameLabel(def.label) }))}
              items={d.field === "materials" ? withMaterialCompositionPercentage(dedupeMaterials(dpp[d.field])) : dpp[d.field]}
              pagination={d.field === "materials"}
            />
          </Box>
        ))}
      </>
    );
  };

  const active = sectionList.find((s) => s.key === selected) || sectionList[0];

  const renderActiveContent = () => {
    if (active.key === "photo") {
      if (entityId) {
        if (!photos.length) {
          return <Typography sx={{ fontSize: "12.5px", color: "#94a3b8", fontFamily: "Poppins, sans-serif", textAlign: "left" }}>{t("No photo uploaded.")}</Typography>;
        }
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {photos.map((p) => (
              <Box key={p.id} sx={{ width: 160, height: 160, borderRadius: tok.radius.card, overflow: "hidden", border: `1px solid ${tok.border.card}`, flexShrink: 0 }}>
                <img src={p.url} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </Box>
            ))}
          </Box>
        );
      }
      if (!photoPreview) {
        return <Typography sx={{ fontSize: "12.5px", color: "#94a3b8", fontFamily: "Poppins, sans-serif", textAlign: "left" }}>{t("No photo uploaded.")}</Typography>;
      }
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <img src={photoPreview} style={{ objectFit: "cover", maxWidth: "100%", height: "auto", borderRadius: tok.radius.card }} alt={renameLabel("Product")} />
        </Box>
      );
    }
    if (active.key === "hosting") {
      return (
        <>
          {sectionBody(active.dppSection)}
          {dpp?.id && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <QRCodeCanvas
                value={myUrl}
                size={200}
                bgColor="#ffffff"
                fgColor="#000000"
                level="H"
                marginSize={10}
                imageSettings={{
                  src: foot,
                  x: undefined,
                  y: undefined,
                  height: 22,
                  width: 22,
                  opacity: 1,
                  excavate: true,
                }}
              />
            </Box>
          )}
        </>
      );
    }
    return sectionBody(active.dppSection);
  };

  if (fullMode) {
    const printSections = sectionList.filter((s) => s.key !== "photo");
    return (
      <Box sx={{ backgroundColor: "#fff", display: "flex", flexDirection: "column", gap: "18px", textAlign: "left" }}>
        {/* Cover block — captured and placed as its own image by generateDppPdf */}
        <Box
          id="dpp-pdf-cover"
          sx={{
            borderRadius: "14px",
            p: "28px 32px",
            background: `linear-gradient(135deg, ${GREEN} 0%, #3f6d1d 100%)`,
            color: "#fff",
          }}
        >
          <Typography sx={{ fontSize: "10.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.16em", color: "rgba(255,255,255,0.85)" }}>
            {t(renameLabel("Digital Product Passport"))}
          </Typography>
          <Typography sx={{ mt: 1, fontSize: "28px", fontWeight: 700, lineHeight: 1.25, color: "#fff", wordBreak: "break-word" }}>
            {dpp?.[nameField] || t(renameLabel("Untitled product"))}
          </Typography>
          <Typography sx={{ mt: 1, fontSize: "11px", color: "rgba(255,255,255,0.75)" }}>
            {t("Generated on")} {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </Typography>
        </Box>

        {/* Product photo + QR code, right below the cover */}
        {(photos.length > 0 || qrValue) && (
          <Box
            id="dpp-pdf-hero"
            sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", p: "20px", display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: "24px" }}
          >
            {photos.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 130px)", gap: "10px" }}>
                  {photos.map((p) => (
                    <Box key={p.id} sx={{ width: 130, height: 130, borderRadius: "10px", overflow: "hidden", border: "1px solid #e5e7eb" }}>
                      <img src={p.url} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b" }}>
                  {t(renameLabel(photos.length > 1 ? "Product Photos" : "Product Photo"))}
                </Typography>
              </Box>
            )}
            {qrValue && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                <QRCodeCanvas
                  value={qrValue}
                  size={180}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="H"
                  marginSize={10}
                  imageSettings={{
                    src: foot,
                    x: undefined,
                    y: undefined,
                    height: 20,
                    width: 20,
                    opacity: 1,
                    excavate: true,
                  }}
                />
                <Typography sx={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b" }}>
                  {t("Scan for Digital Passport")}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* One captured block per section — kept off the same canvas as the
            cover/other sections so generateDppPdf can place each on whichever
            page it actually fits on, instead of slicing mid-table/mid-row. */}
        {printSections.map((s, i) => (
          <Box key={s.key} id={`dpp-pdf-section-${s.key}`} sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", p: "18px 20px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "10px", pb: "10px", borderBottom: `2px solid ${GREEN}` }}>
              <Box sx={{ width: 24, height: 24, borderRadius: "7px", backgroundColor: GREEN, color: "#fff", display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 700, flexShrink: 0 }}>
                {i + 1}
              </Box>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#111827", fontFamily: tok.font.family }}>
                {t(renameLabel(s.label))}
              </Typography>
            </Box>
            {sectionBody(s.dppSection, true)}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2, textAlign: "left" }}>
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", lg: "320px 1fr" }, alignItems: "start" }}>
        {/* ── Left nav ── */}
        <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: tok.radius.card, overflow: "hidden", alignSelf: "start", p: 2 }}>
          <Typography sx={{ mb: 1.5, fontWeight: 600, fontSize: "15px", color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>
            {t("Passport Contents")}
          </Typography>
          <Box component="ol" sx={{ display: "grid", gap: 0.75, m: 0, p: 0, listStyle: "none" }}>
            {sectionList.map((s, i) => {
              const Icon = s.icon;
              const isActive = s.key === selected;
              return (
                <Box component="li" key={s.key} sx={{ listStyle: "none" }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSelected(s.key)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25, width: "100%", textAlign: "left",
                      border: `1px solid ${isActive ? "rgba(101,157,51,0.4)" : tok.border.card}`,
                      backgroundColor: isActive ? (isDark ? "rgba(101,157,51,0.15)" : "rgba(101,157,51,0.10)") : "transparent",
                      borderRadius: "6px", px: 1.5, py: 1, cursor: "pointer", fontFamily: tok.font.family,
                      "&:hover": { backgroundColor: isActive ? undefined : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)") },
                    }}
                  >
                    <Box sx={{ width: 24, height: 24, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: "11px", fontWeight: 600, flexShrink: 0, backgroundColor: isActive ? GREEN : (isDark ? "rgba(101,157,51,0.15)" : "rgba(101,157,51,0.12)"), color: isActive ? "#fff" : GREEN }}>
                      {i + 1}
                    </Box>
                    <Icon size={16} color={GREEN} style={{ flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, fontSize: tok.font.size.bodyText, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameLabel(s.label))}</Typography>
                    <LuChevronRight size={16} color={isActive ? GREEN : tok.text.muted} style={{ flexShrink: 0 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* ── Content ── */}
        <Paper sx={{ border: 1, borderColor: tok.border.card, borderRadius: tok.radius.card, p: 0, pt: 3, pb: 3 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 1.5, mb: 2, pb: 1.5, px: 3, borderBottom: `1px solid ${tok.border.card}` }}>
            <Box sx={{ textAlign: "left" }}>
              <Typography sx={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", color: GREEN, fontFamily: tok.font.family, textAlign: "left" }}>
                {t(renameLabel("Digital Product Passport"))} • {t("Preview")}
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: "20px", fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameLabel(active.label))}</Typography>
              <Typography sx={{ fontSize: tok.font.size.bodyText, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "left" }}>{t(renameLabel(active.blurb))}</Typography>
            </Box>
            {dpp?.[nameField] && (
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontWeight: 600, color: tok.text.primary, fontFamily: tok.font.family, textAlign: "right" }}>{t(renameLabel("Product"))}</Typography>
                <Typography sx={{ fontSize: tok.font.size.caption, color: tok.text.muted, fontFamily: tok.font.family, textAlign: "right" }}>{dpp[nameField]}</Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ px: 3 }}>{renderActiveContent()}</Box>
        </Paper>
      </Box>
    </Box>
  );
};
export default ProductDPP_Preview;
